(ns shared.core.components.recharts.tooltip
  (:require [clojure.string :as s]
            [shared.core.components.icon :as icon]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(def tooltip-pointer-offset 10)
(def tooltip-viewport-padding 16)
(def default-tooltip-width 240)
(def tooltip-overflow-offset 16)

(defn footer-separator [{:keys [style]}]
  [:div {:style (merge {:background-color "rgba(0, 0, 0, 0.06)"
                        :margin "6px 0"
                        :height 1
                        :width "100%"}
                       style)}])

(defn double-click-tooltip-display [{:keys [style]}]
  [:div {:style (merge {:color (style/alc-color "Text/secondary")
                        :display :flex
                        :align-items :center
                        :flex-wrap :wrap}
                       style)}
   [icon/material-icon {:icon-type :ads-click
                        :style {:color :inherit
                                :font-size 12}}]
   [:span {:style {:color :inherit
                   :margin-left 6}}
    "Double click to view details (hold"]
   [:div {:style {:background-color (style/alc-color "Background/card_hex")
                  :border-radius 3
                  :color :inherit
                  :font-size 11
                  :display :flex
                  :align-items :center
                  :justify-content :center
                  :margin "0 4px"
                  :padding "0 3px"}}
    (if (util/mac?) "⌘" "ctrl")]
   [:span {:style {:color :inherit}}
    "to open in a new tab)"]])

(defn- parse-size
  [value]
  (cond
    (number? value) value
    (and (string? value)
         (not (s/includes? value "%")))
    (let [match (re-find #"-?\d+(?:\.\d+)?" value)]
      (when match
        (js/parseFloat match)))
    :else nil))

(defn- estimate-tooltip-width
  [style-map]
  (or (some #(parse-size (get style-map %))
            [:tooltip-width :width :max-width :min-width])
      default-tooltip-width))

(defn floating-style
  "Convenience helper to position tooltips relative to the mouse cursor."
  [x y & [extra-style]]
  (let [extra-style (or extra-style {})
        viewport-width (cond
                         (exists? js/window) (.-innerWidth js/window)
                         (exists? js/document) (.-clientWidth (.-documentElement js/document))
                         :else 0)
        raw-left (+ x tooltip-pointer-offset)
        max-left (if (pos? viewport-width)
                   (max tooltip-viewport-padding viewport-width)
                   raw-left)
        overflow? (> raw-left max-left)
        overflow-left (max tooltip-viewport-padding
                           (- max-left tooltip-overflow-offset))
        target-left (if overflow? overflow-left raw-left)
        clamped-left (-> target-left
                         (min max-left)
                         (max tooltip-viewport-padding))
        cleaned-style (dissoc extra-style :tooltip-width :tooltip-height)]
    (merge {:position :fixed
            :z-index 9999
            :opacity 1
            :left (str clamped-left "px")
            :top (str (+ y tooltip-pointer-offset) "px")}
           cleaned-style)))

(defn- indicator-style
  [{:keys [color shape size]}]
  (merge {:width (or size 8)
          :height (or size 8)
          :border-radius (case shape
                           :square 2
                           :rounded 4
                           :circle "50%"
                           :line 2
                           "50%")
          :background-color color
          :flex "0 0 auto"}
         (when (= shape :square)
           {:border-radius 2})
         ;; A line series' swatch reads as a line, not a block (matches the legend).
         (when (= shape :line)
           {:width (or size 10)
            :height 3})))

(defn tooltip-panel [{:keys [style-map header rows numeric-align column-count column-template sort-by-value? custom-sort
                             limit value-transf-fn show-percent? percent-style show-double-click-message?
                             skip-translate-x?]
                      :or {show-percent? true
                           limit 10}}]
  (let [left-val (some-> style-map :left (util/parse-int))
        top-val (some-> style-map :top (util/parse-int))
        translate-x (when (and (not skip-translate-x?) (some-> left-val (> (/ (.-clientWidth js/document.body) 2))))
                      (str "calc(-100% - " (* tooltip-pointer-offset 2) "px)"))
        translate-y (when top-val (str (* (/ top-val (.-clientHeight js/document.body)) -100) "%"))
        column-template (or column-template "minmax(140px,1fr) 1fr")
        numeric-align (or numeric-align :left)
        numeric-right? (= numeric-align :right)
        header? (and header (not (false? (:show? header))))
        header-label (:label header)
        header-description (:description header)
        header-value (:value header)
        header-percent (:percent header)
        container-style (cond-> (merge {:display :flex
                                        :flex-direction :column
                                        :gap 2
                                        :background (style/alc-color "Background/up_level")
                                        :padding 6
                                        :font-family style/roboto-font-family
                                        :font-size 12
                                        :line-height 1.5
                                        :border-radius 6
                                        :border (str "1px solid " (style/alc-color "Border/divider"))
                                        :box-shadow "0px 8px 24px rgba(0, 0, 0, 0.12)"
                                        :pointer-events :none
                                        :min-width 240}
                                       style-map)
                                (and translate-x translate-y)
                                (assoc :transform (str "translate(" translate-x "," translate-y ")"))

                                (and translate-x (not translate-y))
                                (assoc :transform (str "translateX(" translate-x ")"))

                                (and (not translate-x) translate-y)
                                (assoc :transform (str "translateY(" translate-y ")")))
        row-base-style {:display :grid
                        :grid-template-columns column-template
                        :column-gap 6
                        :align-items :center
                        :padding 3
                        :border-radius 4}
        limited-rows (if limit (take limit rows) rows)
        row-nodes (mapv (fn [{:keys [label value percent color highlight? indicator highlight-color key]}]
                          (let [value (if value-transf-fn (value-transf-fn value) value)
                                percent (when show-percent? percent)
                                font-weight (if highlight? style/font-bold-weight style/font-regular-weight)
                                resolved-key (or key (str label "|" value "|" percent))
                                highlight-bg (or highlight-color
                                                 (when (and color (s/starts-with? color "#"))
                                                   (util/hex->rgba color 0.12)))
                                row-style (cond-> row-base-style
                                                  (and highlight? highlight-bg) (assoc :background-color highlight-bg))
                                indicator-style-map (when indicator
                                                      (indicator-style {:color (or (get indicator :color) color)
                                                                        :shape (or (get indicator :shape) :circle)
                                                                        :size (or (get indicator :size) 8)}))]
                            [:div {:key resolved-key
                                   :style row-style}
                             [:div {:style {:display :flex
                                            :align-items :center
                                            :gap 6}}
                              (when indicator-style-map
                                [:div {:style indicator-style-map}])
                              (when label
                                [:div {:style {:color (style/alc-color "Text/primary_header")
                                               :font-family style/roboto-font-family
                                               :font-size 12
                                               :line-height "18px"
                                               :letter-spacing 0
                                               :font-weight font-weight
                                               :flex 1}}
                                 label])]
                             [:span {:style (cond-> {:color (style/alc-color "Text/secondary")
                                                     :font-size 12
                                                     :font-weight font-weight
                                                     :line-height 1.5
                                                     :display :flex
                                                     :align-items :center
                                                     :justify-self :end
                                                     :flex "0 0 auto"}
                                                    numeric-right?
                                                    (assoc :text-align :right))}
                              (when (not-empty value)
                                [:span {:style {:flex "0 0 auto"
                                                :color :inherit}}
                                 value])
                              (when (not-empty percent)
                                (let [percent (cond
                                                (= percent "100.0%")
                                                "100%"

                                                (not (s/ends-with? percent "%"))
                                                (str percent "%")

                                                :else percent)]
                                  [:span {:style (merge {:flex "0 0 42px"
                                                         :color :inherit
                                                         :text-align :left
                                                         :margin-left 3}
                                                        percent-style)}
                                   (str "(" percent ")")]))]]))
                        (cond
                          custom-sort
                          (sort custom-sort limited-rows)

                          sort-by-value?
                          (sort (fn [a b]
                                  (> (some-> a :value (util/parse-int))
                                     (some-> b :value (util/parse-int))))
                                limited-rows)

                          :else
                          limited-rows))]
    [:div {:style container-style}
     (when header?
       (let [header-val (s/trim (str (or header-value "")
                                     (when (not-empty header-percent)
                                       (str " (" header-percent ")"))))]
         [:div
          [:div {:style (cond-> {:font-size 14
                                 :font-weight style/font-bold-weight
                                 :color (style/alc-color "Text/primary_header")
                                 :padding 3}
                                (not-empty header-val)
                                (assoc :display :grid
                                       :grid-template-columns column-template
                                       :column-gap 12))}
           [:span {:style {:justify-self :start}} (or header-label "")]
           (when (not-empty header-val)
             [:span {:style (cond-> {:justify-self :end}
                                    numeric-right? (assoc :text-align :right))}
              header-val])]
          (when (not-empty header-description)
            [:div {:style {:color (style/alc-color "Text/secondary")
                           :font-size 12
                           :font-weight 400
                           :line-height 1.2
                           :padding "0 3px 3px"}}
             header-description])]))
     (into [:div {:style (cond-> {}
                                 (> column-count 1)
                                 (merge {:display :grid
                                         :grid-template-columns (s/join " " (repeat column-count "1fr"))
                                         :column-gap 24}))}]
           row-nodes)
     (when (and limit (> (count rows) limit))
       [:div {:style {:color (style/alc-color "Text/secondary")
                      :font-size 12
                      :line-height "18px"}}
        (str "+ " (- (count rows) limit) " More")])
     (when show-double-click-message?
       [:div
        (when (or header? (not-empty rows))
          [footer-separator {}])
        [double-click-tooltip-display {}]])]))

(defn chart-tooltip-content
  "Returns a recharts tooltip `:content` render fn shared across the cartesian charts (line,
   area, radar, scatter). Renders a `tooltip-panel` with one row per payload series, each with a
   colored square indicator and a readable-number value. Options:
   - :header-fn  (fn [{:keys [label payload]}]) -> the panel header label (defaults to the axis label)
   - :label-fn   (fn [series-name]) -> transformed row label
   - :value-fn   (fn [value]) -> transformed row value (else numbers are readable-number'd)
   - :row-fn     (fn [row]) -> post-transform applied to each built row. The row carries
                 `:series-name` (the untransformed series) so it can format per series.
   - :min-width  panel min width (default 160)"
  ([] (chart-tooltip-content nil))
  ([{:keys [header-fn label-fn value-fn row-fn min-width]
     :or {min-width 160}}]
   (fn [props]
     (let [{:keys [active payload label]} (js->clj props :keywordize-keys true)]
       (re/as-element
         (when (and active (not-empty payload))
           (let [rows (mapv (fn [{:keys [name value stroke color fill]}]
                              (let [swatch (or stroke color fill)
                                    row {:series-name name
                                         :label (if label-fn (label-fn name) name)
                                         :value (cond
                                                  value-fn (value-fn value)
                                                  (number? value) (util/readable-number value)
                                                  :else (str value))
                                         :color swatch
                                         :indicator {:color swatch
                                                     :shape :square}}]
                                (if row-fn (row-fn row) row)))
                            payload)]
             [tooltip-panel
              {:style-map {:min-width min-width}
               :header {:label (if header-fn (header-fn {:label label :payload payload}) label)}
               :rows rows
               :show-percent? false}])))))))